import { z, ZodTypeAny } from "zod";
import { FieldType } from "@shared/features/form/Form";

export function buildFormSchemaFromFields(fields: FieldType[]) {
  const shape: Record<string, ZodTypeAny> = {};

  for (const field of fields) {
    const {
      name,
      required,
      type,
      validation_rules: rules,
      choices,
      isForceYes,
    } = field;

    let schema: ZodTypeAny;
    const requiredMsg = "This field is required";

    switch (type) {
      case "text input": {
        schema = z.string();

        if (required) {
          schema = schema.min(1, { message: requiredMsg });
        } else {
          schema = schema.optional();
        }

        if (rules?.min_characters != null) {
          schema = schema.min(rules.min_characters, {
            message: `Minimum ${rules.min_characters} characters`,
          });
        }

        if (rules?.max_characters != null) {
          schema = schema.max(rules.max_characters, {
            message: `Maximum ${rules.max_characters} characters`,
          });
        }
        break;
      }

      case "yes/no":
      case "yes/no-buttons":
      case "multiple choice": {
        if (isForceYes) {
          schema = z.any();
          break;
        }

        const validChoices = choices?.map((choice) => choice.id);

        schema = z
          .array(z.number())
          // Required
          .refine((arr) => !required || arr.length > 0, {
            message: "Please select at least one option",
          })
          // All values must be valid if any are present
          .refine(
            (arr) => {
              // allow empty array
              if (arr.length === 0) return true;

              return arr.every((val) => validChoices?.includes(val));
            },
            {
              message: "Please select valid option(s)",
            }
          );

        if (rules?.min_select != null) {
          schema = schema.refine((arr) => arr.length >= rules.min_select!, {
            message: `Select at least ${rules.min_select} option(s)`,
          });
        }

        if (rules?.max_select != null && rules.max_select !== "none") {
          schema = schema.refine((arr) => arr.length <= rules.max_select!, {
            message: `Select up to ${rules.max_select} option(s)`,
          });
        }

        // Optional schema wrapper, only if the field is not required
        if (!required) {
          schema = schema.optional();
        }

        break;
      }

      case "organization selector": {
        const entrySchema = z.object({
          organization: z
            .union([
              z.string().min(1),
              z.object({
                label: z.string().min(1),
                value: z.string().min(1),
                data: z.any().optional(),
              }),
              z.null(),
              z.undefined(),
            ])
            .optional(),

          year: z
            .array(z.string().min(1))
            .optional()
            .or(z.null())
            .or(z.undefined()),
        });

        schema = z
          .array(entrySchema)
          .refine((arr) => arr.some((entry) => !!entry.organization), {
            message: "Please add at least one organization",
          });

        if (!required) {
          schema = schema.optional();
        }

        break;
      }

      default: {
        schema = z.any();
        break;
      }
    }

    shape[name] = schema;
  }

  return z.object(shape);
}
